import React, { useState, useEffect } from "react";
import {
  FaChartLine,
  FaBoxOpen,
  FaUsers,
  FaBuilding,
  FaList,
  FaCloudUploadAlt,
  FaBox,
  FaTasks,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import * as dashboardService from "../services/dashboard.service";

const actionIcon = (action) => {
  if (action === "client_created" || action === "client_updated") return FaUsers;
  if (action === "product_created" || action === "product_updated") return FaBox;
  if (action === "document_uploaded") return FaCloudUploadAlt;
  return FaTasks;
};

const actionColor = (action) => {
  if (action?.startsWith("client")) return "success";
  if (action?.startsWith("product")) return "info";
  if (action === "document_uploaded") return "warning";
  return "secondary";
};

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const DashboardHome = () => {
  const { user } = useAuth();
  const currentHour = new Date().getHours();
  const greeting =
    currentHour < 12
      ? "Good Morning"
      : currentHour < 18
        ? "Good Afternoon"
        : "Good Evening";

  const [stats, setStats] = useState({
    totalClients: 0,
    totalProducts: 0,
  });
  const [activities, setActivities] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await dashboardService.getDashboardStats();
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setStats({ totalClients: 0, totalProducts: 0 });
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await dashboardService.getRecentActivities(15);
        if (!cancelled) setActivities(rows);
      } catch {
        if (!cancelled) setActivities([]);
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const quickActions = [
    {
      icon: FaBoxOpen,
      label: "Add Product",
      path: "/dashboard/product/add-product",
      color: "primary",
      description: "Create new product",
    },
    {
      icon: FaUsers,
      label: "Add Client",
      path: "/dashboard/crm-master/add-client",
      color: "info",
      description: "Register new client",
    },
    {
      icon: FaList,
      label: "Client List",
      path: "/dashboard/crm-master/client-list",
      color: "success",
      description: "View all clients",
    },
    {
      icon: FaChartLine,
      label: "Catalogue",
      path: "/dashboard/product/catalog",
      color: "warning",
      description: "Product catalogue",
    },
    {
      icon: FaBox,
      label: "Client Catalogue",
      path: "/dashboard/product/client-catalog",
      color: "danger",
      description: "Client-specific view",
    },
    {
      icon: FaBuilding,
      label: "Company Details",
      path: "/dashboard/company-details",
      color: "secondary",
      description: "Company info",
    },
    {
      icon: FaCloudUploadAlt,
      label: "DMS Library",
      path: "/dashboard/dms",
      color: "dark",
      description: "Document management",
    },
  ];

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="col-lg-3 col-md-6 mb-3">
      <div
        className={`stat-card bg-${color}-light border border-${color} rounded-3 p-4 d-flex align-items-center gap-4`}
      >
        <div className="stat-icon">
          <Icon className={`text-${color}`} />
        </div>
        <div className="flex-fill">
          <p className="small fw-medium text-secondary mb-0">{title}</p>
          <h3 className="stat-value fw-bold text-dark mt-1 mb-0">
            {overviewLoading ? "—" : value}
          </h3>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-home">
      <div className="welcome-card mb-5 p-5 rounded-4 text-white">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className="fs-1 mb-3">👋</div>
            <h1 className="h3 fw-bold mb-2">
              {greeting}, {user?.name}!
            </h1>
            <p className="mb-0 opacity-75">
              Welcome back to your CRM Dashboard. Let's make today productive!
            </p>
          </div>
          <div className="welcome-illustration d-none d-md-block">📊</div>
        </div>
      </div>

      <div className="mb-4">
        <h5 className="fw-bold text-dark fs-5 mb-4">Dashboard Overview</h5>
        <div className="row g-3">
          <StatCard
            title="Total Clients"
            value={stats.totalClients}
            icon={FaUsers}
            color="primary"
          />
          <StatCard
            title="Total Products"
            value={stats.totalProducts}
            icon={FaBox}
            color="info"
          />
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h5 className="fw-bold text-dark fs-5 mb-0">Quick Actions</h5>
          <small className="text-muted">Access key features instantly</small>
        </div>
        <div className="row g-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <div key={index} className="col-lg-4 col-md-6">
                <Link
                  to={action.path}
                  className="quick-action-card text-decoration-none"
                >
                  <div
                    className={`card border-0 h-100 shadow-sm overflow-hidden quick-action-inner quick-action-border-${action.color}`}
                  >
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <div
                          className={`action-icon d-flex align-items-center justify-content-center me-3 bg-${action.color} bg-opacity-10 p-3 rounded-2`}
                        >
                          <Icon className={`text-${action.color}`} size={24} />
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="card-title mb-1 text-dark fw-semibold">
                            {action.label}
                          </h6>
                          <p className="card-text text-muted small mb-0">
                            {action.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h5 className="fw-bold text-dark fs-5 mb-4">Recent Activity</h5>
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {activityLoading && (
              <p className="text-muted small mb-0">Loading activity…</p>
            )}
            {!activityLoading && activities.length === 0 && (
              <p className="text-muted small mb-0">
                No recent activity yet. Actions such as adding clients, products,
                or DMS uploads will appear here.
              </p>
            )}
            {!activityLoading &&
              activities.map((item, index) => {
                const Icon = actionIcon(item.action);
                const color = actionColor(item.action);
                const isLast = index === activities.length - 1;
                return (
                  <div
                    key={item.id ?? index}
                    className={`activity-item d-flex align-items-center pb-3 mb-3 ${!isLast ? "border-bottom" : "mb-0 pb-0"}`}
                  >
                    <div
                      className={`activity-icon d-flex align-items-center justify-content-center bg-${color} bg-opacity-10 p-2 rounded-circle me-3`}
                    >
                      <Icon className={`text-${color}`} />
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-0 text-dark">
                        <strong>{item.title}</strong>
                        {item.description && (
                          <span className="text-muted">
                            {" "}
                            — {item.description}
                          </span>
                        )}
                      </p>
                      <small className="text-muted">
                        {formatWhen(item.at)}
                        {item.userName ? ` · ${item.userName}` : ""}
                      </small>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
